import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Zap, ArrowRight } from "lucide-react";

/**
 * "Today's Same Day Lineup" — horizontal carousel of products flagged
 * `is_same_day=true`. Renders nothing if the lineup is empty so the homepage
 * doesn't show an awkward placeholder block.
 */
export default function SameDayCarousel() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let off = false;
    api.get("/products/same-day-lineup")
      .then(({ data }) => { if (!off) setItems(data || []); })
      .catch(() => { if (!off) setItems([]); });
    return () => { off = true; };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section className="bg-white border-t border-slate-200" data-testid="home-same-day-carousel">
      <div className="container-page py-10 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[#DD6B33] mb-1">
              <Zap className="w-4 h-4" fill="currentColor" />
              <div className="overline">Today&apos;s Same Day Lineup</div>
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-black text-kolt-inkwell">
              Order by 11 AM CT · Ships Today
            </h2>
          </div>
          <Link
            to="/same-day"
            data-testid="same-day-see-all"
            className="font-mono text-[11px] tracking-[0.2em] uppercase font-bold text-[#DD6B33] hover:text-[#9A3F12] flex items-center gap-1.5"
          >
            View entire lineup <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2 snap-x snap-mandatory">
          {items.map((p) => (
            <Link
              key={p.product_id}
              to={`/shop/product/${p.slug}`}
              data-testid={`same-day-card-${p.slug}`}
              className="snap-start shrink-0 w-[260px] group block border border-slate-200 hover:border-[#DD6B33] transition-colors relative"
            >
              {/* Same-Day pip */}
              <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-[#DD6B33] text-white text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm">
                <Zap className="w-2.5 h-2.5" fill="currentColor" /> Same Day
              </div>
              <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                <img
                  src={p.image_url}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                />
              </div>
              <div className="p-3">
                <div className="font-display font-black text-base text-kolt-inkwell leading-tight line-clamp-2">{p.name}</div>
                {p.same_day_subtitle ? (
                  <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mt-1 line-clamp-1">
                    {p.same_day_subtitle}
                  </div>
                ) : p.short_description ? (
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.short_description}</div>
                ) : null}
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-sm font-bold text-kolt-inkwell">
                    from <span className="tabular-nums">${(p.base_price || 0).toFixed(2)}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#DD6B33] group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
